import type {
  ApiNodeDataBranch,
  BranchComparison,
  BranchComparisonType,
  GraphOperationUpdateNode,
  NodeConfigurationContextBranch,
} from '@onefootprint/request-types/dashboard';
import type { NodeProps } from '@/playbook-builder/playbook-builder.types';
import type { BranchFormData, FormBranchComparison } from '../../types';
import getBranch from '../get-branch';

const toBranchComparison = (kind: BranchComparisonType, c: FormBranchComparison): BranchComparison => {
  switch (kind) {
    case 'bool':
      return { op: c.op, value: c.value === 'true' };
    case 'number':
      return { op: c.op, value: Number(c.value) };
    case 'string':
      return { op: c.op, value: c.value };
  }
};

const createPayload = (
  data: BranchFormData,
  node: NodeProps<ApiNodeDataBranch>,
  config: NodeConfigurationContextBranch,
): GraphOperationUpdateNode => {
  const existingCases = node.data?.cases || [];
  const existingCaseIds = new Set(existingCases.map(c => c.id));
  const newCaseIds = new Set(data.cases.map(c => c.branchId));

  const selectedPredicate = getBranch(data.branchPredicateId, config?.config);
  if (!selectedPredicate) {
    throw new Error('Selected predicate not found');
  }

  const toAdd = data.cases
    .filter(c => !existingCaseIds.has(c.branchId))
    .map(c => ({
      condition: toBranchComparison(selectedPredicate?.kind, c.condition),
      branchName: c.branch.name,
    }));
  const toDelete = existingCases.filter(c => !newCaseIds.has(c.id)).map(c => c.id);
  const toUpdate = data.cases
    .filter(c => existingCaseIds.has(c.branchId))
    .map(c => ({
      id: c.branchId,
      condition: toBranchComparison(selectedPredicate?.kind, c.condition),
      branch: c.branch,
    }));

  return {
    kind: 'update_node' as const,
    nodeId: node.id,
    nodeData: {
      kind: 'branch' as const,
      branchOnExpression: selectedPredicate?.identifier,
      add: toAdd,
      delete: toDelete,
      update: toUpdate,
      defaultBranchName: data.defaultBranch ?? undefined,
    },
  };
};

export default createPayload;
